import { useState } from "react";
import BottomSheet from "./BottomSheet";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const SOURCES = ["Sportsbet", "Neds", "Ladbrokes", "TAB", "Manual"];

export default function EnterResultModal({ open, onClose, group, race, onSaved }) {
  const isEdit = !!race?.result;
  const [first, setFirst] = useState(race?.result?.first_horse_number || "");
  const [second, setSecond] = useState(race?.result?.second_horse_number || "");
  const [third, setThird] = useState(race?.result?.third_horse_number || "");
  const [win, setWin] = useState(race?.result?.win_dividend ?? "");
  const [place, setPlace] = useState(race?.result?.place_dividend ?? "");
  const [second_, setSecond_] = useState(race?.result?.second_dividend ?? "");
  const [thirdPaid, setThirdPaid] = useState(race?.result?.third_dividend_paid ?? true);
  const [thirdDiv, setThirdDiv] = useState(race?.result?.third_dividend ?? "");
  const [source, setSource] = useState(race?.result?.source || "Sportsbet");
  const [saving, setSaving] = useState(false);

  if (!race) return null;
  const liveHorses = race.horses.filter((h) => !h.scratched);

  const submit = async () => {
    if (!first || !second || !third) { toast.error("Pick 1st, 2nd, 3rd"); return; }
    if (String(first) === String(second) || String(first) === String(third) || String(second) === String(third)) {
      toast.error("1st/2nd/3rd must all be different"); return;
    }
    if (win === "" || place === "" || second_ === "") { toast.error("Enter Win/Place/2nd dividends"); return; }
    if (thirdPaid && thirdDiv === "") { toast.error("Enter 3rd dividend or toggle 'not paid'"); return; }

    const payload = {
      first_horse_number: Number(first),
      second_horse_number: Number(second),
      third_horse_number: Number(third),
      win_dividend: Number(win),
      place_dividend: Number(place),
      second_dividend: Number(second_),
      third_dividend_paid: !!thirdPaid,
      third_dividend: thirdPaid ? Number(thirdDiv) : null,
      source,
    };
    setSaving(true);
    try {
      const method = isEdit ? "patch" : "post";
      const { data } = await api[method](`/groups/${group.id}/races/${race.id}/result`, payload);
      toast.success(`Result saved · ${data.picks_scored} pick${data.picks_scored === 1 ? "" : "s"} scored`);
      onSaved && onSaved(data.race);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Save failed"));
    } finally { setSaving(false); }
  };

  const clearResult = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      await api.delete(`/groups/${group.id}/races/${race.id}/result`);
      toast.success("Result cleared — race back to open");
      onSaved && onSaved(null);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Clear failed"));
    } finally { setSaving(false); }
  };

  const horseOpts = liveHorses.map((h) => (
    <option key={h.horse_number} value={h.horse_number}>#{h.horse_number} · {h.name} @ {h.odds.toFixed(2)}</option>
  ));

  return (
    <BottomSheet open={open} onClose={onClose} title={isEdit ? "Edit result" : "Enter result"} testId="enter-result-modal">
      <div className="space-y-4">
        <div className="text-xs font-bold text-[#6c757d]">
          {race.track} · R{race.race_number} · {race.race_name}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[["1st", first, setFirst, "result-1st"], ["2nd", second, setSecond, "result-2nd"], ["3rd", third, setThird, "result-3rd"]].map(([label, v, s, tid]) => (
            <div key={label}>
              <div className="text-[10px] font-black uppercase tracking-wider text-[#6c757d] mb-1">{label}</div>
              <select value={v} onChange={(e) => s(e.target.value)} data-testid={tid}
                      className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-2 py-2 text-sm font-medium">
                <option value="">—</option>
                {horseOpts}
              </select>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[["Win $", win, setWin, "win-div"], ["Place $", place, setPlace, "place-div"], ["2nd $", second_, setSecond_, "second-div"]].map(([label, v, s, tid]) => (
            <div key={label}>
              <div className="text-[10px] font-black uppercase tracking-wider text-[#6c757d] mb-1">{label}</div>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={v} onChange={(e) => s(e.target.value)}
                     data-testid={tid}
                     className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2 text-sm font-bold text-center" />
            </div>
          ))}
        </div>

        <label className="flex items-center gap-3 bg-[#f8f9fa] rounded-xl p-3 cursor-pointer">
          <input type="checkbox" checked={thirdPaid} onChange={(e) => setThirdPaid(e.target.checked)}
                 className="w-5 h-5 accent-[#1971c2]" data-testid="third-paid-toggle" />
          <div>
            <div className="font-bold text-sm">3rd place dividend paid?</div>
            <div className="text-[10px] text-[#6c757d]">
              {thirdPaid ? "Enter 3rd $ below." : "Displayed as 'No 3rd Dividend — 0 pts' (red)."}
            </div>
          </div>
        </label>

        {thirdPaid && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-wider text-[#6c757d] mb-1">3rd $</div>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={thirdDiv}
                   onChange={(e) => setThirdDiv(e.target.value)} data-testid="third-div"
                   className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2 text-sm font-bold text-center" />
          </div>
        )}

        <div>
          <div className="text-[10px] font-black uppercase tracking-wider text-[#6c757d] mb-1">Dividend source</div>
          <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="dividend-source"
                  className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2 text-sm font-bold">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="flex gap-2">
          {isEdit && (
            <button onClick={clearResult} disabled={saving}
                    className="bg-[#fdecec] text-[#c92a2a] font-bold rounded-xl py-3 px-4 border-2 border-[#c92a2a]/25"
                    data-testid="result-clear">
              Clear
            </button>
          )}
          <button onClick={submit} disabled={saving}
                  className="flex-1 bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl py-3 active:scale-95 disabled:opacity-40"
                  data-testid="result-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save result")}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
